import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Text,
  useDisclosure,
} from '@chakra-ui/react';

import UploadVideoModal from '../components/vidoes/UploadVideoModal';
import useApi from '../lib/hooks/useApi';
import urlConstants from '../lib/constants/url.constants';
import useToastNotification from '../lib/hooks/useToastNotification';

import { uploadVideoToVimeo } from '../lib/utils';
import { ApiCommand } from '../lib/Api';
import StrongInput from '../components/common/StrongInput';
import Page from '../components/common/Page';
import { MdDelete } from 'react-icons/md';

const VIMEO_ACCESS_TOKEN = process.env.REACT_APP_VIMEO_ACCESS_TOKEN || '';
const { createVideo: createVideoUrl, getVideos: getVideosUrl, deleteVideo: deleteVideoUrl } =
  urlConstants.videos;

const Videos = () => {
  const [videos, setVideos] = useState<Video[]>([]);
  const [search, setSearch] = useState('');
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isVideoUploading, setIsVideoUploading] = useState<boolean>(false);

  const showToast = useToastNotification();

  const { loading: isGetVideosLoading, sendRequest: getVideos } =
    useApi<Video[]>();
  const { loading: isCreateVideoLoading, sendRequest: createVideo } =
    useApi<any>();
  const { loading: isDeleteCategoryLoading, sendRequest: deleteVideo } =
    useApi<any>();

  const filteredVideos = videos.filter(
    (video) =>
      video.title.toLowerCase().includes(search.toLowerCase()) ||
      video.description.toLowerCase().includes(search.toLowerCase())
  );

  const fetchVideos = () => {
    getVideos({
      callback: (data: Video[] | null, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        if (!data) return null;
        setVideos(data);
      },
      command: ApiCommand.GET,
      url: getVideosUrl,
    });
  };

  const onDeleteVideo = (videoId: string) => {
    console.log("videoId:::", videoId);
    deleteVideo({
      callback: (_data, error: string | null) => {
        if (error) {
          showToast({
            title: 'Failed',
            description: error,
            status: 'error',
          });
          return;
        }
        showToast({
          title: 'Success',
          description: 'Video deleted successfully',
          status: 'success',
        });
        fetchVideos();
      },
      command: ApiCommand.DELETE,
      url: deleteVideoUrl,
      options: {
        videoId,
      },
    });
  };

  useEffect(() => {
    fetchVideos();
  }, []);

  const onVideoUpload = async (
    title: string,
    description: string,
    videoFile: File | null
  ) => {
    if (videoFile) {
      setIsVideoUploading(true);
      const vimeoId = await uploadVideoToVimeo(
        title,
        description,
        videoFile,
        VIMEO_ACCESS_TOKEN
      );
      setIsVideoUploading(false);
      if (vimeoId) {
        createVideo({
          callback: (_data, error: string | null) => {
            if (error) {
              showToast({
                title: 'Failed',
                description: error,
                status: 'error',
              });
              return;
            }
            showToast({
              title: 'Success',
              description: 'Equipment created successfully',
              status: 'success',
            });
            onClose();
            fetchVideos();
          },
          command: ApiCommand.POST,
          url: createVideoUrl,
          options: {
            title,
            description,
            vimeoId: vimeoId,
          },
        });
      }
    }
  };

  return (
      <Page>
      <Flex justifyContent='space-between' alignItems='center' mb={6}>
        <Heading size='lg'>Video Library</Heading>
        <Button colorScheme='primary' onClick={onOpen}>
          Create Video
        </Button>
      </Flex>

      <Box mb={6}>
        <StrongInput
          placeholder='Search videos...'
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Box>

      <Flex
        wrap='wrap'
        justify='center'
        gap={4}
      >
        {filteredVideos.map((video, index) => (
          <Box
            key={index}
            bg='white'
            borderRadius='lg'
            overflow='hidden'
            boxShadow='lg'
            maxWidth='350px'
            width='100%'
            flex='1 1 300px'
          >
            <Box
              as='iframe'
              src={`https://player.vimeo.com/video/${video.vimeoId}`}
              width='100%'
              height='200px'
              borderRadius='lg'
              frameBorder='0'
              allow='autoplay; fullscreen; picture-in-picture'
              allowFullScreen
              title={video.title}
            />
            <Flex align="center" justify="space-between" bg='gray.50' p={4}>
              <Box >
                <Heading
                  as='h3'
                  size='md'
                  color='primary.600'
                  fontWeight='semibold'
                  mb={2}
                >
                  {video.title}                  
                </Heading>
                <Text fontSize='sm' color='gray.600' noOfLines={3}>
                  {video.description}
                </Text>              
              </Box>
              <IconButton
                aria-label='Delete Category'
                icon={<MdDelete />}
                variant='outline'
                colorScheme='red'
                size='sm'
                onClick={() => onDeleteVideo(video.id)}
              />
            </Flex>
            
          </Box>
        ))}
      </Flex>

      {filteredVideos.length === 0 && (
        <Text textAlign='center' color='gray.600' mt={6}>
          No videos found.
        </Text>
      )}

      <UploadVideoModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={onVideoUpload}
        isLoading={isVideoUploading || isCreateVideoLoading}
      />

      </Page>
  );
};

export default Videos;
